import VoiceAPI from 'dashboard/api/voice.js';
import { mapGetters } from 'vuex';
import {
  CALL_DIRECTION,
  CALL_STATUS,
  CALL_REMOTE_STATUS,
} from 'components/widgets/modal/constants';
import alertMixin from 'shared/mixins/alertMixin';
import ringtoneFile from '../assets/audio/ringtone-call.mp3';
import ringbackToneFile from '../assets/audio/ringback-tone.mp3';
import unifiedAudioService from './unifiedAudioService';

export default {
  mixins: [alertMixin],
  data() {
    return {
      // durationInterval: null,
      audioContext: null,
      audioWebSocket: null,
      isConnectingAudio: false,
      // Track call_ids that have been processed to ignore duplicates
      processedCallIds: new Set(),
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      getAccount: 'accounts/getAccount',
      unifiedCall: 'call/getUnifiedCall',
      unifiedUI: 'call/getUnifiedUI',
    }),
    currentAccount() {
      return this.getAccount(this.currentUser.account_id);
    },
  },
  async mounted() {
    if (this.unifiedCall.initializedMixingOnce) {
      // initialize once to prevent duplicate cable connection
      return;
    }
    // Setup global unlock
    this.setupGlobalAudioUnlock();

    this.initAudioSystem();

    // Register bus listener
    this._onCallChannelReceiveEvent = data => this.handleChannelResponse(data);
    bus.$on('onCallChannelReceiveEvent', this._onCallChannelReceiveEvent);
    this.$store.dispatch('call/updateUnifiedCall', {
      initializedMixingOnce: true,
    });
  },
  beforeDestroy() {
    if (this._onCallChannelReceiveEvent) {
      bus.$off('onCallChannelReceiveEvent', this._onCallChannelReceiveEvent);
      this._onCallChannelReceiveEvent = null;
    }
  },
  methods: {
    async initAudioSystem() {
      // 1️⃣ Setup audio context
      this.setupAudioContext();

      // 2️⃣ Request mic permission immediately
      // await this.requestMicrophonePermission();
      // await this.startMicrophone();

      // 3️⃣ Start listening for incoming calls (and audio)
      // this.listenForIncomingCalls(); // no need as action cable will automatically listen event
      // await this.connectToAudioStream();  // this one shuold call after receive incomming call
    },
    setupAudioContext() {
      // this.audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      // this.nextStartTime = 0;
      if (this.audioContext && this.audioContext.state === 'closed') {
        this.audioContext = unifiedAudioService.getContext(); // shared instance
      }

      this.$store.dispatch('call/updateUnifiedCall', {
        nextStartTime: 0,
      });
    },

    async handleChannelResponse(data) {
      const { currentCallId, callStatus } = this.unifiedCall;
      const isRunning = [
        CALL_STATUS.INITIALIZING,
        CALL_STATUS.DIALING,
        CALL_STATUS.RINGING,
      ].includes(callStatus);

      switch (data.type) {
        case 'incoming_call':
          this.handleIncomingCall(data);
          break;
        case 'audio_chunk':
          this.playAudio64(data.audio);
          break;
        case 'call_answered': {
          // Determine if we should connect to audio
          const shouldConnect =
            (this.unifiedCall.callDirection === CALL_DIRECTION.OUTGOING &&
              data.audio_ws_url) || // Outgoing: remote answered
            (this.unifiedCall.callDirection === CALL_DIRECTION.INCOMING &&
              this.unifiedCall.isAccepted); // Incoming: we accepted

          if (shouldConnect) {
            // Connect to Node.js audio WebSocket
            if (data.audio_ws_url) {
              this.connectAudioWebSocket(data.audio_ws_url);
            }

            this.$store.dispatch('call/updateUnifiedCall', {
              callStatus: CALL_STATUS.CONNECTED, // change status to connected
              isAccepting: false, // Clear accepting flag
              awaitingLocalResponse: false, // Clear awaiting response flag
              wasConnected: true, // Track that call was connected
            });
            this.stopRingtone();
            this.startCallTimer();
          } else if (
            this.unifiedCall.callDirection === CALL_DIRECTION.INCOMING &&
            !this.unifiedCall.isAccepted
          ) {
            // For incoming calls where we didn't accept - another device accepted
            this.stopRingtone();
            this.cleanup();
            this.$store.dispatch('call/updateUnifiedCall', {
              callActive: false,
            });
          }
          break;
        }

        case 'call_cancelled':
          if (data.call_id === currentCallId && isRunning) {
            this.stopRingtone();
            this.cleanup();
            this.resetState();
          }

          break;
        case 'call_ended':
          this.endCall();
          break;

        case 'call_terminated': {
          // Determine final status based on reason
          let finalStatus = CALL_STATUS.COMPLETED;

          if (
            data.reason === 'caller_hangup' ||
            data.reason === 'remote_hangup'
          ) {
            finalStatus = CALL_STATUS.DISCONNECTED;
          }

          // remove subscription from action cable
          // keep status and duration to pass with evaluation
          this.$root.actionCable?.unsubscribeCallChannel();
          this.$store.dispatch('call/updateUnifiedCall', {
            callStatus: finalStatus,
            callActive: false, // IMPORTANT: Mark call as inactive
            evaluationInfo: {
              status: data.status,
              duration: data.duration,
              reason: data.reason,
            },
          });
          this.stopRingtone();
          this.cleanup();
          // Don't call resetState() here - let the evaluation screen handle it
          break;
        }

        case 'status_update':
          this.handleStatusUpdate(data);
          break;
        // case 'call_accepted':
        //   this.$store.dispatch('call/updateUnifiedCall', {
        //     callStatus: CALL_STATUS.CONNECTED,
        //   });
        //   this.startCallTimer();
        //   break;
        case 'call_error':
          this.$store.dispatch('call/updateUnifiedCall', {
            callStatus: CALL_STATUS.FAILED,
          });
          this.endCall();
          break;
        default:
      }
    },
    initAudio() {
      this.audioContext = unifiedAudioService.getContext(); // shared instance
    },
    stopAudio() {
      unifiedAudioService.closeContext();
    },
    async connectToAudioStream() {
      const { currentCallId } = this.unifiedCall;
      await this.$root.actionCable?.subscribeCallChannel(currentCallId);
    },

    // --------------- Handle Incomming call--------------
    async handleIncomingCall(callData) {
      const { currentCallId } = this.unifiedCall;

      // Ignore duplicate incoming_call events
      if (this.processedCallIds.has(callData.call_id)) {
        return;
      }

      // Mark as processed
      this.processedCallIds.add(callData.call_id);

      // Also check currentCallId
      if (currentCallId && currentCallId !== callData.call_id) {
        // Allow new call if previous call is in terminal state (completed, failed, disconnected)
        const isTerminalState = [
          CALL_STATUS.COMPLETED,
          CALL_STATUS.FAILED,
          CALL_STATUS.DISCONNECTED,
        ].includes(this.unifiedCall.callStatus);

        if (!isTerminalState) {
          return;
        }
        // Previous call was terminal, clean up and proceed with new call
        this.cleanup();
        this.resetState();
      }

      this.$store.dispatch('call/updateUnifiedCall', {
        provider: callData.provider,
        callActive: true,
        isMuted: false,
        currentCallId: callData.call_id,
        incomingCall: callData,
        callStatus: CALL_STATUS.RINGING, // ringing could be better
        callDirection: CALL_DIRECTION.INCOMING,
        nextStartTime: 0,
        contact: {
          ...(callData.contact || {}),
          inbox_id: callData.inbox_id,
        },
      });

      // IMPORTANT: Subscribe to AudioChannel immediately so both devices receive call_accepted events
      // This allows devices that don't accept to know when another device accepted
      await this.$root.actionCable?.subscribeCallChannel(callData.call_id);

      this.playRingtone();
    },
    async acceptCall() {
      const { incomingCall, contact, provider } = this.unifiedCall;

      if (!incomingCall) {
        return;
      }

      try {
        // Set loading state and accepted flag
        this.$store.dispatch('call/updateUnifiedCall', {
          isAccepting: true,
          isAccepted: true,
        });

        this.stopRingtone();

        // AudioChannel is already subscribed in handleIncomingCall
        // No need to call connectToAudioStream() again
        // Just send answer to backend
        const answerPayload = {
          id: incomingCall.call_id,
          provider: provider || incomingCall?.provider,
          contact_id: contact?.id || incomingCall?.contact?.id,
          inbox_id: incomingCall?.inbox_id,
          account_id: this.currentUser.account_id,
        };
        this.$root.actionCable?.receiveIncomingCall(answerPayload);

        // Delay 300ms to ensure local accept is processed before we wait for call_answered
        await new Promise(resolve => setTimeout(resolve, 300));

        // Update Vuex state after delay
        this.$store.dispatch('call/updateUnifiedCall', {
          incomingCall: null,
          callActive: true,
          // callStatus: CALL_STATUS.CONNECTED, // wait untill remote connect
        });
        this.stopRingtone();

        // this.startCallTimer();  // may call after get accept event

        // navigate to contact
        if (contact.id) {
          const targetRoute = {
            name: 'contact_profile_dashboard',
            params: {
              accountId: this.currentUser.account_id,
              contactId: contact.id,
            },
          };
          if (this.$route.name !== targetRoute.name) {
            this.$router.push(targetRoute);
          }
        }
      } catch (error) {
        console.error('Error accepting incoming call:', error);
        // Clear loading state on error
        this.$store.dispatch('call/updateUnifiedCall', {
          isAccepting: false,
          isAccepted: false,
        });
      }
    },
    async rejectCall() {
      console.log('🚫 [CALL DEBUG] rejectCall START');
      const { incomingCall, currentCallId } = this.unifiedCall;
      if (!incomingCall || !currentCallId) {
        console.warn(
          '⚠️ [CALL DEBUG] rejectCall: No incomingCall or currentCallId'
        );
        return;
      }

      try {
        // Set loading state
        this.$store.dispatch('call/updateUnifiedCall', {
          isRejecting: true,
        });

        this.stopRingtone();

        await this.$root.actionCable?.subscribeCallChannel(currentCallId);
        this.$root.actionCable?.rejectCall(currentCallId);

        this.stopMicrophone();
        this.cleanup();
        this.resetState();
      } catch (error) {
        console.error('⚠️ [CALL DEBUG] Error rejecting incoming call:', error);
        // Clear loading state on error
        this.$store.dispatch('call/updateUnifiedCall', {
          isRejecting: false,
        });
      }
    },
    // --------------- Handle incoming call end -------------

    // --------------- New websocket connection ------------
    connectAudioWebSocket(url) {
      // Prevent duplicate connection attempts
      if (this.isConnectingAudio || this.audioWebSocket) {
        return;
      }

      // Set flag IMMEDIATELY (before async WebSocket creation)
      this.isConnectingAudio = true;
      this.audioWebSocket = new WebSocket(url);

      this.audioWebSocket.onopen = () => {
        this.isConnectingAudio = false; // ← Clear flag on success
        this.startMicrophone();
      };

      this.audioWebSocket.onmessage = event => {
        const msg = JSON.parse(event.data);
        if (msg.action === 'audio_chunk') {
          // Pass dynamic sample_rate from Node.js message
          this.playAudio64(msg.audio_base_64, msg.sample_rate);
        }
      };

      this.audioWebSocket.onerror = error => {
        console.error('Audio WebSocket error:', error);
        this.isConnectingAudio = false; // ← Clear flag on error
      };

      this.audioWebSocket.onclose = () => {
        this.stopMicrophone();
        this.audioWebSocket = null;
        this.isConnectingAudio = false; // ← Clear flag on close
      };
    },
    // --------------- Handle outgoing call -------------
    async makeCall(provider, contact = {}) {
      this.$store.dispatch('call/updateUnifiedCall', {
        callActive: true,
        callStatus: CALL_STATUS.DIALING,
        callDirection: CALL_DIRECTION.OUTGOING,
        provider,
        contact,
      });

      try {
        const payload = {
          inbox_id: contact.inbox_id,
          contact_id: contact.id,
          // type: "whatsapp"
          type: provider,
        };
        const { data } = await VoiceAPI.initiate_call(payload);
        // console.log(data,"initiate_call");
        if (!data.success) {
          this.showAlert(data.error || 'Failed to start call');
          this.cleanup();
          this.resetState();
          return;
        }

        // this.currentCallId = data.call_id;
        const call_id = data.uuid;
        this.$store.dispatch('call/updateUnifiedCall', {
          currentCallId: call_id,
        });

        // subscribe to cable
        // await this.$root.actionCable?.subscribeCallChannel(call_id);

        this.connectToAudioStream();
        // this.startMicrophone();
        this.playRingtone();

        this.$store.dispatch('call/updateUnifiedCall', {
          callStatus: CALL_STATUS.DIALING,
          callActive: true,
        });
        // this.showStatus('Initiating call...', 'info');
      } catch (error) {
        console.log(error);
        // this.showStatus('Failed to initiate call', 'error');
        // this.connecting = false;
        this.$store.dispatch('call/updateUnifiedCall', {
          connecting: false,
          callStatus: CALL_STATUS.FAILED,
        });
      }
    },
    async endCall() {
      const {
        currentCallId,
        callActive,
        callStatus,
        callDirection,
      } = this.unifiedCall; // get from Vuex

      //
      // if outgoing && call didnt accept, use API call to end call
      if (
        currentCallId &&
        callActive &&
        callDirection === 'outgoing' &&
        [CALL_STATUS.DIALING, CALL_STATUS.RINGING].includes(callStatus)
      ) {
        try {
          await VoiceAPI.end_call(currentCallId);
        } catch (error) {
          console.error('endCall: API end_call failed:', error);
          this.showAlert(error.error || 'Failed to end call');
        }
      }
      // Send terminate request if call is active
      if (currentCallId && callActive) {
        this.stopRingtone();

        this.$root.actionCable?.terminateCall(currentCallId);
        if (callStatus === CALL_STATUS.CONNECTED) {
          this.$store.dispatch('call/updateUnifiedCall', {
            callStatus: CALL_STATUS.COMPLETED,
          });
        } else {
          this.$store.dispatch('call/updateUnifiedCall', {
            callStatus: CALL_STATUS.DISCONNECTED,
          });
        }
      }

      this.stopMicrophone();
      unifiedAudioService.closeContext();
      unifiedAudioService.resetAll();

      // this will be done when close all
      this.cleanup();
      // this.resetState();
    },
    async redialCall() {
      // Prevent multiple clicks
      if (this.unifiedCall.isRedialing) {
        return;
      }

      this.$store.dispatch('call/updateUnifiedCall', {
        isRedialing: true,
      });

      const oldCall = { ...this.unifiedCall };

      this.$store.dispatch('call/resetUnifiedUI');
      unifiedAudioService.closeContext(); // ensure old context is closed
      //  Re-initialize audio context fresh
      unifiedAudioService.getContext();

      await this.makeCall(oldCall.provider, oldCall.contact);

      // Reset redialing state after a short delay to ensure smooth transition
      setTimeout(() => {
        this.$store.dispatch('call/updateUnifiedCall', {
          isRedialing: false,
        });
      }, 500);
    },
    // --------------- Handle outgoing call end -------------

    // ----------- audio setup and ringron control-------------
    playRingtone() {
      let { ringtoneAudio } = this.unifiedUI;

      // Use ringback-tone for outgoing calls, ringtone-call for incoming calls
      const audioFile =
        this.unifiedCall.callDirection === CALL_DIRECTION.OUTGOING
          ? ringbackToneFile
          : ringtoneFile;

      if (!ringtoneAudio) {
        // Create new Audio if not exists
        ringtoneAudio = new Audio(audioFile);
        ringtoneAudio.loop = true;
        this.$store.dispatch('call/updateUnifiedUI', { ringtoneAudio });
      } else if (ringtoneAudio.src !== audioFile) {
        ringtoneAudio.src = audioFile;
      }

      ringtoneAudio.play().catch(err => {
        console.warn('Ringtone play failed:', err);
      });
    },
    stopRingtone() {
      const { ringtoneAudio } = this.unifiedUI;
      if (ringtoneAudio) {
        ringtoneAudio.pause();
        ringtoneAudio.currentTime = 0;
      }
    },
    unlockAudio() {
      if (this.unifiedUI.ringtoneAudio) return; // already unlocked

      const audio = new Audio(ringtoneFile);
      audio.loop = true;

      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          this.$store.dispatch('call/updateUnifiedUI', {
            ringtoneAudio: audio,
          });
        })
        .catch(err => {
          console.warn('Audio unlock failed:', err);
        });

      // remove the listener after first click
      document.removeEventListener('click', this.unlockAudioHandler);
    },
    setupGlobalAudioUnlock() {
      // save a reference to remove later
      this.unlockAudioHandler = this.unlockAudio.bind(this);
      document.addEventListener('click', this.unlockAudioHandler, {
        once: true,
      });
    },
    // ----------- audio setup and ringron control end-------------
    async startMicrophone() {
      try {
        // Get microphone
        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        // Create AudioContext
        unifiedAudioService.micStream = micStream;
        const audioContext = unifiedAudioService.getContext();

        // Load AudioWorklet
        await audioContext.audioWorklet.addModule('/mic-capture-processor.js');

        // Create AudioWorklet node
        const audioWorkletNode = new AudioWorkletNode(
          audioContext,
          'mic-capture-processor'
        );

        // Receive audio from worklet
        audioWorkletNode.port.onmessage = event => {
          const { buffer } = event.data;
          // Convert to base64
          const base64Audio = this.arrayBufferToBase64(buffer);

          // Send to Node.js
          const audioPayload = {
            action: 'audio_chunk',
            audio_base_64: base64Audio,
          };
          this.audioWebSocket.send(JSON.stringify(audioPayload));
        };

        // Connect microphone -> worklet -> destination
        const source = audioContext.createMediaStreamSource(micStream);
        source.connect(audioWorkletNode);
        audioWorkletNode.connect(audioContext.destination); // Optional: for monitoring
      } catch (err) {
        console.error('Failed to start microphone:', err);
        console.error('Error name:', err.name);
        console.error('Error message:', err.message);

        if (err.name === 'NotAllowedError') {
          alert(
            'Microphone permission denied. Please allow microphone access.'
          );
        } else if (err.name === 'NotFoundError') {
          alert('No microphone found.');
        } else if (err.message.includes('module')) {
          alert('Failed to load audio processor. Check file path.');
        }
      }
    },

    stopMicCapture() {
      const { audioContext, audioWorkletNode, micStream } = unifiedAudioService;

      if (audioWorkletNode) {
        audioWorkletNode.disconnect();
        audioWorkletNode = null;
      }

      if (audioContext) {
        audioContext.close();
        audioContext = null;
      }

      if (micStream) {
        micStream.getTracks().forEach(track => track.stop());
        micStream = null;
      }
    },

    // ✅ NEW: Efficient base64 encoding without spread operator
    arrayBufferToBase64(buffer) {
      const bytes = new Uint8Array(buffer);
      const chunkSize = 8192; // Process in 8KB chunks to avoid memory spike
      let binary = '';

      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
        binary += String.fromCharCode.apply(null, chunk);
      }

      return btoa(binary);
    },

    // ✅ NEW: Downsample function for input audio
    downsample(buffer, fromRate, toRate) {
      if (fromRate === toRate) return buffer;

      const ratio = fromRate / toRate;
      const outputLength = Math.floor(buffer.length / ratio);
      const output = new Float32Array(outputLength);

      for (let i = 0; i < outputLength; i++) {
        const position = i * ratio;
        const index = Math.floor(position);
        const fraction = position - index;

        if (index + 1 < buffer.length) {
          output[i] =
            buffer[index] * (1 - fraction) + buffer[index + 1] * fraction;
        } else {
          output[i] = buffer[index];
        }
      }

      return output;
    },

    // ✅ NEW: Convert Float32 to PCM with guaranteed little-endian
    floatToPCM(float32Array) {
      const buffer = new ArrayBuffer(float32Array.length * 2);
      const view = new DataView(buffer);

      for (let i = 0; i < float32Array.length; i++) {
        const s = Math.max(-1, Math.min(1, float32Array[i]));
        const val = s < 0 ? s * 0x8000 : s * 0x7fff;
        view.setInt16(i * 2, val, true); // true = little-endian
      }

      return buffer;
    },

    sendAudioToBackend(base64Audio) {
      const { currentCallId } = this.unifiedCall;
      const audioPayload = {
        id: currentCallId,
        audio: base64Audio,
      };
      this.$root.actionCable?.sendAudioToBackend(audioPayload);
    },

    playAudio64(base64Audio, sourceSampleRate = 16000) {
      const audioService = unifiedAudioService;
      sourceSampleRate;

      try {
        if (
          !audioService.audioContext ||
          audioService.audioContext?.state === 'closed'
        ) {
          audioService.getContext();
          audioService.nextStartTime = 0;
        }

        // --- 1. DECODE Base64 to Float32 ---
        const binary = atob(base64Audio);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }

        const int16 = new Int16Array(bytes.buffer);
        const float32 = new Float32Array(int16.length);
        for (let i = 0; i < int16.length; i++) {
          float32[i] = int16[i] / 32768.0;
        }

        // --- 2. RESAMPLE IF NEEDED ---
        const targetSampleRate = audioService.audioContext.sampleRate;
        let finalAudio = float32;

        // Resample if source and target rates differ
        if (sourceSampleRate !== targetSampleRate) {
          finalAudio = this.resample(
            float32,
            sourceSampleRate,
            targetSampleRate
          );
        }

        // --- 3. CREATE AUDIO BUFFER ---
        // Create buffer using the AudioContext's rate (16000Hz)
        const buffer = audioService.audioContext.createBuffer(
          1,
          finalAudio.length,
          targetSampleRate
        );
        buffer.getChannelData(0).set(finalAudio);

        // --- 4. PLAY WITH SCHEDULING ---
        const source = audioService.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(audioService.audioContext.destination);

        const now = audioService.audioContext.currentTime;
        let startTime = audioService.nextStartTime;

        // ⭐ IMPROVED: Handle stale nextStartTime
        if (!startTime || startTime < now) {
          startTime = now + 0.01; // Start NOW + 10ms for quick recovery
        }

        source.start(startTime);
        audioService.nextStartTime = startTime + buffer.duration;

        this.updateAudioLevel(finalAudio);
      } catch (error) {
        console.error('Error playing audio:', error);
      }
    },

    resample(inputBuffer, inputRate, outputRate) {
      const ratio = outputRate / inputRate;
      const outputLength = Math.round(inputBuffer.length * ratio);
      const output = new Float32Array(outputLength);

      for (let i = 0; i < outputLength; i++) {
        const position = i / ratio;
        const index = Math.floor(position);
        const fraction = position - index;

        if (index + 1 < inputBuffer.length) {
          output[i] =
            inputBuffer[index] * (1 - fraction) +
            inputBuffer[index + 1] * fraction;
        } else {
          output[i] = inputBuffer[index];
        }
      }

      return output;
    },

    updateAudioLevel(audioData) {
      let sum = 0;
      for (let i = 0; i < audioData.length; i += 100) {
        sum += audioData[i] * audioData[i];
      }
      const rms = Math.sqrt(sum / (audioData.length / 100));
      unifiedAudioService.audioLevel = Math.min(100, rms * 200);
    },

    stopMicrophone() {
      const audioService = unifiedAudioService;

      try {
        // Reset chunk counter for next call
        this.audioChunkCount = 0;
        audioService.nextStartTime = 0;

        if (audioService.audioProcessor) {
          audioService.audioProcessor.disconnect();

          // Clean up AudioWorklet port listener
          if (audioService.audioProcessor.port) {
            audioService.audioProcessor.port.onmessage = null;
          }

          // Clean up deprecated ScriptProcessor callback
          if (audioService.audioProcessor.onaudioprocess) {
            audioService.audioProcessor.onaudioprocess = null;
          }

          audioService.audioProcessor = null;
        }

        if (audioService.micStream) {
          audioService.micStream.getTracks().forEach(track => track.stop());
          audioService.micStream = null;
        }

        // if (audioService.audioContext && audioService.audioContext.state !== "closed") {
        //   audioService.audioContext.suspend().catch(err => console.warn("Audio context suspend failed:", err));
        // }

        if (audioService.audioContext) {
          const ctx = audioService.audioContext;
          if (ctx.state !== 'closed') {
            ctx
              .close()
              .catch(err => console.warn('AudioContext close failed:', err));
          }
          audioService.audioContext = null;
        }

        // console.log("🎤 Microphone stopped successfully");
      } catch (err) {
        console.error('Error stopping microphone:', err);
      }
    },
    // ----------- Mic and audio control end-------------

    // Cleanup and timer
    startCallTimer() {
      const { durationInterval } = this.unifiedUI;
      if (durationInterval) {
        clearInterval(durationInterval);
        this.$store.dispatch('call/updateUnifiedUI', {
          durationInterval: null,
        });
      }

      const startTime = Date.now();
      const interval = setInterval(() => {
        const duration = Math.floor((Date.now() - startTime) / 1000);
        this.$store.dispatch('call/updateUnifiedUI', {
          callDuration: duration,
        });
      }, 1000);

      this.$store.dispatch('call/updateUnifiedUI', {
        callStartTime: startTime,
        durationInterval: interval,
      });
    },

    cleanup() {
      const { durationInterval } = this.unifiedUI;

      // Stop call timer
      if (durationInterval) {
        clearInterval(durationInterval);
        this.$store.dispatch('call/updateUnifiedUI', {
          durationInterval: null,
        });
      }
    },

    resetState() {
      this.$store.dispatch('call/resetUnifiedCall');
      this.$store.dispatch('call/resetUnifiedUI');
    },

    // handle backend status sync
    handleStatusUpdate(data) {
      switch (data.status) {
        case CALL_REMOTE_STATUS.RINGING:
          this.$store.dispatch('call/updateUnifiedCall', {
            callStatus: CALL_STATUS.RINGING,
          });
          break;
        case CALL_REMOTE_STATUS.ACCEPTED:
          // Check if this is a local accept (we initiated) or remote accept (another device)
          if (this.unifiedCall.awaitingLocalResponse) {
            // Local accept in progress - wait for call_answered event
          } else {
            // Another device accepted - hide call card
            this.stopRingtone();
            this.cleanup();
            this.resetState();
          }
          break;
        case CALL_REMOTE_STATUS.REJECTED:
          this.endCall();
          break;
        default:
      }
    },
    // end methods
  },
};
